import React from 'react';
import type { ShortcutCombination } from './keymap';

const LETTER_CODE_PATTERN = /^Key([A-Z])$/;
const DIGIT_CODE_PATTERN = /^Digit([0-9])$/;

/** A `code`-matched key as the key it prints on a Latin keycap: `KeyL` → `L`, `Digit1` → `1`. */
const keyOf = (combination: ShortcutCombination): string => {
    if (combination.matchOn === 'key') {
        return combination.value;
    }
    return (
        LETTER_CODE_PATTERN.exec(combination.value)?.[1] ??
        DIGIT_CODE_PATTERN.exec(combination.value)?.[1] ??
        combination.value
    );
};

/**
 * A combination in `aria-keyshortcuts` syntax: the modifiers by their ARIA
 * names, then the key, joined with `+` — `F9`, `Control+Delete`,
 * `Control+Shift+L`. Named keys keep their `KeyboardEvent.key` value, which
 * is what ARIA expects.
 */
export const toAriaKeyShortcuts = (combination: ShortcutCombination): string =>
    [
        combination.ctrlKey ? 'Control' : null,
        combination.altKey ? 'Alt' : null,
        combination.shiftKey ? 'Shift' : null,
        combination.metaKey ? 'Meta' : null,
        keyOf(combination),
    ]
        .filter((part) => part !== null)
        .join('+');

/**
 * The `render` prop for a design-system control whose key its
 * `ShortcutHint` shows (a bound shortcut, or a dialog footer's `Enter` and
 * `Esc`), putting `aria-keyshortcuts` on the element it renders. Build it
 * once at module level, so the control doesn't get a new render function on
 * every render. `Button`,
 * `ListView` and `Dropdown.Item` drop the attribute when it is passed as a
 * prop — react-aria forwards only an allowlist of DOM props — and `render`
 * is their documented way to reach the element itself. `element` is the tag
 * the control renders (`'button'` for `Button`, `'div'` for a grid list or a
 * menu item).
 */
export const renderWithKeyShortcuts =
    (element: 'button' | 'div', combination: ShortcutCombination) =>
    (props: object): React.ReactElement =>
        React.createElement(element, {
            ...props,
            'aria-keyshortcuts': toAriaKeyShortcuts(combination),
        });
